/**
 * OpenSincera lookups shared by the publishers table, the add-publisher dialog and the discovery page:
 * the per-domain query (cached by the API for a day), the score and player badges, and the lookup card.
 */
import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { errorMessage, isApiError } from '@/api/client';
import type { PublisherLookupOut } from '@/api/types';
import { Badge, type BadgeTone } from '@/components/arc/badge/badge';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { useApi } from '@/module/MomentProvider';
import styles from './overlay.module.css';

const DAY_MS = 24 * 60 * 60 * 1000;

/** `https://www.Yemek.com/x` -> `yemek.com`, mirroring the API's normalize_domain. */
export function normalizeDomain(value: string): string {
  let d = value.trim().toLowerCase();
  if (d.includes('://')) d = d.slice(d.indexOf('://') + 3);
  d = d.split(/[/?#]/)[0] ?? '';
  if (d.startsWith('www.')) d = d.slice(4);
  return d.replace(/^\.+|\.+$/g, '');
}

export function looksLikeDomain(d: string): boolean {
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d);
}

export function usePublisherLookup(domain: string) {
  const api = useApi();
  const d = normalizeDomain(domain);
  return useQuery({
    queryKey: ['publisher-lookup', d],
    queryFn: ({ signal }) => api.publishers.lookup(d, signal),
    enabled: looksLikeDomain(d),
    staleTime: DAY_MS,
    retry: false,
  });
}

export function scoreTone(score: number): BadgeTone {
  if (score >= 70) return 'success';
  if (score >= 40) return 'info';
  return 'warning';
}

export function fmtPct(v: number | null | undefined): string {
  return v == null ? 'n/a' : `${Math.round(v * 100)}%`;
}

export function fmtNum(v: number | null | undefined, digits = 1): string {
  return v == null ? 'n/a' : v.toFixed(digits);
}

export function ScoreBadge({ score, reasons }: { score: number; reasons?: string[] }) {
  return (
    <Badge tone={scoreTone(score)} size="sm" title={reasons?.join('. ')} className={styles.scoreBadge}>
      {score}
    </Badge>
  );
}

export function PlayerBadge({ ownPlayer, players }: { ownPlayer: boolean; players: string[] }) {
  if (ownPlayer) return <Badge tone="success" size="sm">Own player</Badge>;
  if (players.length) return <Badge tone="warning" size="sm">Embeds only</Badge>;
  return <Badge tone="neutral" size="sm">No player found</Badge>;
}

/** Quality column cell: the prospect score for a publisher row, or a quiet note when Sincera has no record. */
export function QualityCell({ domain }: { domain: string }) {
  const q = usePublisherLookup(domain);
  if (!looksLikeDomain(normalizeDomain(domain))) return <span className="text-muted">n/a</span>;
  if (q.isPending) return <span className={styles.scorePending} aria-label="Loading quality score" />;
  if (q.isError) {
    const status = isApiError(q.error) ? q.error.status : 0;
    return <span className="text-muted">{status === 404 ? 'Not indexed' : 'n/a'}</span>;
  }
  return (
    <span className={styles.qualityCell}>
      <ScoreBadge score={q.data.score} reasons={q.data.reasons} />
      <PlayerBadge ownPlayer={q.data.own_player} players={q.data.players} />
    </span>
  );
}

/** Compact Sincera card under the domain field of the publisher form. Renders nothing until the value looks like a domain. */
export function LookupCard({ domain }: { domain: string }) {
  const d = normalizeDomain(domain);
  const q = usePublisherLookup(domain);
  if (!looksLikeDomain(d)) return null;
  if (q.isPending) {
    return (
      <div className={styles.lookupCard} aria-live="polite">
        <Skeleton lines={3} label={`Looking up ${d}`} />
      </div>
    );
  }
  if (q.isError) {
    const status = isApiError(q.error) ? q.error.status : 0;
    return (
      <div className={styles.lookupCard} aria-live="polite">
        <p className={styles.muted}>
          {status === 404
            ? `${d} is not in the Sincera publisher index. The lookup covers sites with programmatic ads.`
            : status === 503
              ? 'Publisher lookup is not configured on this server.'
              : errorMessage(q.error)}
        </p>
      </div>
    );
  }
  return <LookupSummary data={q.data} />;
}

export function LookupSummary({ data }: { data: PublisherLookupOut }) {
  return (
    <div className={styles.lookupCard} aria-live="polite">
      <div className={styles.lookupHead}>
        <div className={styles.cell}>
          <span className={styles.lookupName}>{data.name ?? data.domain}</span>
          <span className={styles.cellSub}>
            {data.categories.length ? data.categories.join(', ') : 'No category'}
            {data.primary_supply_type ? ` · ${data.primary_supply_type}` : ''}
          </span>
        </div>
        <span className={styles.qualityCell}>
          <ScoreBadge score={data.score} reasons={data.reasons} />
          <PlayerBadge ownPlayer={data.own_player} players={data.players} />
        </span>
      </div>
      <dl className={styles.lookupFacts}>
        <div>
          <dt>Players</dt>
          <dd>{data.players.length ? data.players.join(', ') : 'None detected'}</dd>
        </div>
        <div>
          <dt>Ads to content</dt>
          <dd>{fmtPct(data.a2cr)}</dd>
        </div>
        <div>
          <dt>Ads in view</dt>
          <dd>{fmtNum(data.ads_in_view)}</dd>
        </div>
        <div>
          <dt>Supply paths</dt>
          <dd>{data.supply_paths ?? 'n/a'}</dd>
        </div>
      </dl>
      <div className={styles.lookupFoot}>
        <span className={styles.cellSub}>{data.reasons.join('. ')}</span>
        {data.sincera_url && (
          <a href={data.sincera_url} target="_blank" rel="noreferrer" className={styles.link}>
            Open in Sincera <ExternalLink size={12} strokeWidth={1.75} aria-hidden="true" />
          </a>
        )}
      </div>
    </div>
  );
}
